import { z } from 'zod'
import {
  localeSchema,
  moneySchema,
  optionalText,
  pageQuerySchema,
  quantitySchema,
  versionSchema,
} from './common.js'
import type { SalesGlRole } from './contracting.js'
import type { CostObjectProjectRef } from './cost-objects.js'
import type { OrgUnitRef } from './org-units.js'
import {
  allocationInputSchema,
  type CustomerRef,
  type DocumentBuyer,
  type DrawnRefund,
  discountTypeSchema,
  type InvoiceStatus,
  type LineUnit,
  paymentMethodIdSchema,
  paymentMethodSchema,
  type UserRef,
} from './sales.js'
import { lineSerialsSchema } from './serials.js'

/**
 * Credit notes reduce what a customer owes.
 *   RETURN     goods (or services) coming back from a posted invoice: lines point at invoice lines and
 *              take their prices, discounts and tax from them; stock returns at the cost it left.
 *   ALLOWANCE  a price reduction or goodwill amount, optionally against an invoice; no stock.
 */
export const creditNoteKindSchema = z.enum(['RETURN', 'ALLOWANCE'])
export type CreditNoteKind = z.infer<typeof creditNoteKindSchema>

const positiveQuantity = quantitySchema.refine((v) => Number(v) > 0, 'Quantity must be above zero')

export const returnLineInputSchema = z.object({
  invoiceLineId: z.uuid(),
  quantity: positiveQuantity,
  /** Serial-tracked products: the serials coming back, each one sold on this invoice line. */
  serials: lineSerialsSchema,
})

export const allowanceLineInputSchema = z.object({
  description: z.string().trim().min(1, 'Describe the line').max(500),
  quantity: positiveQuantity,
  unitPrice: moneySchema.refine((v) => Number(v) >= 0, 'Price cannot be negative'),
  discountType: discountTypeSchema.default('PER_UNIT'),
  discountValue: moneySchema.default('0'),
  /** null: no tax. */
  taxRateId: z.uuid().nullable(),
})

const header = {
  customerId: z.uuid(),
  noteDate: z.iso.date(),
  reason: z.string().trim().min(1, 'Give a reason').max(500),
  notes: optionalText(2000),
  /**
   * The owning org unit (ADR-025, BR-SAL-019). Create: omitted = the creator's home unit, null = none.
   * Update: omitted keeps the current unit, only an explicit null clears it. A newly chosen unit must be
   * active; a creator whose scope is not ALL must choose one inside it.
   */
  orgUnitId: z.uuid().nullish(),
  /**
   * The salesperson whose commission it reduces (ADR-049). Create: omitted = the credited invoice's
   * salesperson (else the customer's), null = none. Update: omitted keeps it. Fixed once posted.
   */
  salespersonId: z.uuid().nullish(),
}

const returnNote = z.object({
  kind: z.literal('RETURN'),
  ...header,
  invoiceId: z.uuid(),
  /** Where returned goods go (default: the warehouse the invoice issued from). */
  warehouseId: z
    .uuid()
    .nullish()
    .transform((v) => v ?? null),
  lines: z.array(returnLineInputSchema).min(1, 'Add at least one line').max(500),
})

const allowanceNote = z.object({
  kind: z.literal('ALLOWANCE'),
  ...header,
  invoiceId: z
    .uuid()
    .nullish()
    .transform((v) => v ?? null),
  /**
   * Without an invoice: a CUSTOMER project of the note's customer (projects/02 §3.3). With an invoice
   * the note takes the invoice's project, whatever is sent here; a RETURN always does.
   */
  projectId: z
    .uuid()
    .nullish()
    .transform((v) => v ?? null),
  lines: z.array(allowanceLineInputSchema).min(1, 'Add at least one line').max(500),
})

export const creditNoteInputSchema = z.discriminatedUnion('kind', [returnNote, allowanceNote])
export type CreditNoteInput = z.input<typeof creditNoteInputSchema>
export const creditNoteUpdateSchema = z.discriminatedUnion('kind', [
  returnNote.extend({ version: versionSchema }),
  allowanceNote.extend({ version: versionSchema }),
])
export type CreditNoteUpdate = z.input<typeof creditNoteUpdateSchema>

/**
 * `GET /sales/credit-notes`. `q` matches the note's number, its invoice's number, the reason, the
 * customer's code, and the customer's name and Arabic name with Arabic letter variants folded. Sort
 * keys: CREDIT_NOTE_SORT_FIELDS (default `noteDate`).
 */
export const creditNoteListQuerySchema = pageQuerySchema.extend({
  kind: creditNoteKindSchema.optional(),
  status: z.enum(['DRAFT', 'POSTED', 'VOID']).optional(),
  customerId: z.uuid().optional(),
  invoiceId: z.uuid().optional(),
  from: z.iso.date().optional(),
  to: z.iso.date().optional(),
  /** Posted notes with credit left to refund or allocate (total − allocated − refunded > 0). */
  hasOpenCredit: z.stringbool().optional(),
  /** Notes created by this user; `me` is the caller. */
  createdBy: z.union([z.literal('me'), z.uuid()]).optional(),
  /** Notes credited to this salesperson (ADR-049); `me` is the caller. */
  salespersonId: z.union([z.literal('me'), z.uuid()]).optional(),
  /** Notes tagged to this project. */
  projectId: z.uuid().optional(),
})
export type CreditNoteListQuery = z.input<typeof creditNoteListQuerySchema>

/**
 * Sort keys of the credit-note list and export. `createdAt` orders drafts (they have no number yet);
 * `unallocated` is the credit left (0 unless posted); `customer` the customer's name. Ties: newest first.
 */
export const CREDIT_NOTE_SORT_FIELDS = [
  'noteDate',
  'number',
  'total',
  'createdAt',
  'unallocated',
  'customer',
] as const

/**
 * The credit-note list's view tabs (pages/sales-documents.md §3.6), each the list call beside it:
 *   all         no view filter
 *   drafts      `status=DRAFT`        (suggested sort `-createdAt`)
 *   withCredit  `hasOpenCredit=true`  (suggested sort `noteDate`, oldest first)
 *   void        `status=VOID`
 */
export const CREDIT_NOTE_VIEWS = ['all', 'drafts', 'withCredit', 'void'] as const
export type CreditNoteView = (typeof CREDIT_NOTE_VIEWS)[number]

/** `GET /sales/credit-notes/summary`: the list's filters other than the view (`status`, `hasOpenCredit`). */
export const creditNoteSummaryQuerySchema = creditNoteListQuerySchema.pick({
  q: true,
  kind: true,
  customerId: true,
  invoiceId: true,
  from: true,
  to: true,
  createdBy: true,
  projectId: true,
})
export type CreditNoteSummaryQuery = z.input<typeof creditNoteSummaryQuerySchema>

/**
 * Counts per view for the current filters (one grouped query); each `count` equals the `total` of that
 * view's list call, and `withCredit.unallocated` is Σ `unallocated` of its rows.
 */
export interface CreditNoteSummary {
  all: { count: number }
  drafts: { count: number }
  withCredit: { count: number; unallocated: string }
  void: { count: number }
}

/** `GET /sales/credit-notes/export` (sales.export): the list's filters, view and sort, as CSV. */
export const creditNoteExportQuerySchema = creditNoteListQuerySchema
  .omit({ page: true, pageSize: true })
  .extend({ format: z.enum(['csv']).default('csv'), lang: localeSchema.default('en') })
export type CreditNoteExportQuery = z.input<typeof creditNoteExportQuerySchema>

export const creditNoteAllocateSchema = z.object({
  allocations: z.array(allocationInputSchema).min(1).max(200),
})

export interface CreditNoteListItem {
  id: string
  kind: CreditNoteKind
  number: string | null
  status: 'DRAFT' | 'POSTED' | 'VOID'
  customer: CustomerRef
  /**
   * The invoice the note credits (the trail's upstream slip), with its status and total.
   */
  invoice: { id: string; number: string | null; status: InvoiceStatus; total: string } | null
  /** The project it is tagged to. Always sent by the API; optional in the type only for older fixtures. */
  project?: CostObjectProjectRef | null
  /** Issued by Projects: voided only from the project. Optional in the type only for older fixtures. */
  issuedByProject?: boolean
  noteDate: string
  total: string
  /** Applied to invoices. */
  allocated: string
  /** Paid back to the customer through refunds. */
  refunded: string
  /** The customer's credit left on this note: total − allocated − refunded. */
  unallocated: string
  /** Made by a till return: undone only at the till (`TILL_DOCUMENT`). */
  fromTill: boolean
  /** The user who created the note; null for migrated ones. */
  createdBy: UserRef | null
  /** The salesperson whose commission it reduces (ADR-049). Always sent; optional for older fixtures. */
  salesperson?: UserRef | null
}

export interface CreditNoteLine {
  id: string
  lineNo: number
  invoiceLineId: string | null
  variantId: string | null
  description: string
  quantity: string
  /** A return line keeps its invoice line's unit. Always sent by the API; optional for older fixtures. */
  unit?: LineUnit | null
  unitFactor?: string
  unitPrice: string
  discountType: 'PERCENTAGE' | 'PER_UNIT' | 'LINE_AMOUNT'
  discountValue: string
  taxRateId: string | null
  taxRate: string
  discount: string
  net: string
  tax: string
  total: string
  /** Serial numbers (serial-tracked products; empty otherwise). Always sent by the API; optional in the type only until the web fixtures carry it. */
  serials?: string[]
}

export interface CreditNote extends CreditNoteListItem {
  /** The buyer block for the print (detail only; optional in the type because test fixtures predate it). */
  buyer?: DocumentBuyer
  warehouse: { id: string; code: string } | null
  pricesIncludeTax: boolean
  reason: string
  notes: string | null
  gross: string
  discount: string
  subtotal: string
  tax: string
  lines: CreditNoteLine[]
  allocations: { id: string; invoiceId: string; invoiceNumber: string; amount: string; allocatedOn: string }[]
  journalEntryId: string | null
  voidReason: string | null
  /**
   * The owning org unit (ADR-025); null when none. Always sent by the API; optional in the type only so
   * older fixtures still compile.
   */
  orgUnit?: OrgUnitRef | null
  version: number
  createdAt: string
  updatedAt: string
  /** Refunds that paid this note's credit back (the trail; CN-H2), oldest first. */
  refunds: DrawnRefund[]
  /**
   * A Projects-issued allowance's GL role (the advance reduction debits `customer_advances`, P5): it is
   * voided from the project's Contract tab rather than its Billing tab. Always sent by the API; optional
   * in the type only for older fixtures.
   */
  glRole?: SalesGlRole | null
}

// ── credit in full (G24) ───────────────────────────────────────────────────────

/**
 * `POST /sales/invoices/:id/credit-in-full`: cancels a posted invoice by a credit note dated today
 * (the Muscat business date) for everything not yet credited, instead of voiding it on its own date.
 * `refund`: pay the part the customer had already paid back now, from this treasury account (refunds.create);
 * without it that part stays as customer credit (refused for the walk-in customer, BR-POS-001).
 */
export const creditInFullRefundSchema = z
  .object({
    treasuryAccountId: z.uuid(),
    method: paymentMethodSchema,
    paymentMethodId: paymentMethodIdSchema,
    reference: optionalText(100),
    notes: optionalText(500),
  })
  .superRefine((v, ctx) => {
    if (v.method === 'CHEQUE' && !v.reference) {
      ctx.addIssue({ code: 'custom', path: ['reference'], message: 'Enter the cheque number' })
    }
  })
export const creditInFullSchema = z.object({
  reason: z.string().trim().min(1, 'Give a reason').max(500),
  refund: creditInFullRefundSchema.nullish().transform((v) => v ?? null),
})
export type CreditInFullInput = z.input<typeof creditInFullSchema>

/** `GET /sales/invoices/:id/credit-in-full`: what the command would post, read only. */
export interface CreditInFullPlan {
  invoice: { id: string; number: string; invoiceDate: string; total: string }
  customer: CustomerRef & { isWalkIn: boolean }
  /** Today's business date: the credit note's date. */
  noteDate: string
  /** Every invoice line with something left to credit, at the quantity left. */
  lines: {
    invoiceLineId: string
    description: string
    quantity: string
    unit: LineUnit | null
    /** The serials coming back (serial-tracked lines): exactly those sold on the line and not returned. */
    serials: string[]
    total: string
  }[]
  subtotal: string
  tax: string
  total: string
  /** Applied to the invoice's open balance. */
  appliedToInvoice: string
  /** What the customer had already paid: kept as customer credit, or refunded with `refund`. */
  paidPart: string
  /** Today's period refuses postings (PERIOD_LOCKED, PERIOD_CLOSED, NO_FISCAL_YEAR); null when open. */
  periodProblem: { code: string; message: string } | null
}

export interface CreditInFullResult {
  creditNote: CreditNote
  refund: { id: string; number: string; amount: string } | null
}
